import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import useQuestionnaireStore from "../store/useQuestionnaireStore";
import { ChevronLeft, Smile, Meh, Frown, AlertCircle } from "lucide-react"; // Added Meh icon
import Button from "../components/common/Button";

// Helper to determine color, label, and icon based on intensity
const getPainInfo = (value) => {
  if (value === 0)
    return {
      label: "No Pain",
      color: "#4caf50",
      description: "I feel completely normal.",
      icon: <Smile size={80} strokeWidth={1.5} />,
    };
  if (value <= 3)
    return {
      label: "Mild",
      color: "#8bc34a",
      description: "It's annoying, but I can ignore it.",
      icon: <Smile size={80} strokeWidth={1.5} />,
    };
  if (value <= 6)
    return {
      label: "Moderate",
      color: "#ffc107",
      description: "Interferes with some daily activities.",
      icon: <Meh size={80} strokeWidth={1.5} />,
    };
  if (value <= 9)
    return {
      label: "Severe",
      color: "#ff5722",
      description: "Disabling; unable to perform tasks.",
      icon: <Frown size={80} strokeWidth={1.5} />,
    };
  return {
    label: "Unbearable",
    color: "#f44336",
    description: "Agonizing; immediate help needed.",
    icon: <AlertCircle size={80} strokeWidth={1.5} />,
  };
};

const Step6_Intensity = () => {
  const navigate = useNavigate();
  const { painIntensity, setPainIntensity } = useQuestionnaireStore();

  // Default to existing value or 0
  const [localValue, setLocalValue] = useState(
    painIntensity !== null ? painIntensity : 0
  );

  const painInfo = getPainInfo(localValue);

  const handleChange = (e) => {
    const val = parseInt(e.target.value, 10);
    setLocalValue(val);
    setPainIntensity(val);
  };

  const handleNext = () => {
    console.log("Pain Intensity:", localValue);
    navigate("/step-7");
  };

  return (
    <motion.div
      className="page step6-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="step-header">
        <button onClick={() => navigate(-1)} className="back-button">
          <ChevronLeft size={24} /> Back
        </button>
        <h2>How much pain do you feel normally?</h2>
      </div>

      <div className="intensity-content-wrapper">
        {/* Left Side: Visual Indicator */}
        <motion.div
          className="visual-panel"
          key={localValue} // Triggers animation on change
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 300, damping: 20 }}
          style={{
            backgroundColor: `${painInfo.color}15`, // 15 is hex opacity (approx 8%)
            borderColor: painInfo.color,
          }}
        >
          <div className="icon-wrapper" style={{ color: painInfo.color }}>
            {painInfo.icon}
          </div>
          <div className="score-big" style={{ color: painInfo.color }}>
            {localValue}
          </div>
          <div className="score-subtext">out of 10</div>
        </motion.div>

        {/* Right Side: Controls */}
        <div className="controls-panel">
          <div className="text-content">
            <h3 style={{ color: painInfo.color }}>{painInfo.label}</h3>
            <p>{painInfo.description}</p>
          </div>

          <div className="slider-wide-wrapper">
            <input
              type="range"
              min="0"
              max="10"
              step="1"
              value={localValue}
              onChange={handleChange}
              className="creative-range"
              style={{
                backgroundImage: `linear-gradient(to right, ${
                  painInfo.color
                } 0%, ${painInfo.color} ${(localValue / 10) * 100}%, #e0e0e0 ${
                  (localValue / 10) * 100
                }%, #e0e0e0 100%)`,
              }}
            />
            <div className="slider-ticks-wide">
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                <span
                  key={num}
                  className={num === localValue ? "active" : ""}
                  style={
                    num === localValue
                      ? { color: painInfo.color, cursor: "pointer" }
                      : { cursor: "pointer" }
                  }
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    setLocalValue(num);
                    setPainIntensity(num);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setLocalValue(num);
                      setPainIntensity(num);
                    }
                  }}
                  aria-label={`Set pain to ${num}`}
                >
                  {num}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="navigation-buttons">
        <Button onClick={handleNext}>Next</Button>
      </div>
    </motion.div>
  );
};

export default Step6_Intensity;
